import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import HomeNav from './components/HomeNavbar';
import AuthNav from './components/AuthNav';
import AuthLoading from './components/AuthLoading';
import mode from './components/Mode';
import { logoutUser } from './Slices/auth';

// Lazy-loaded components
const Home = lazy(() => import('./components/Home'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const MemberRegister = lazy(() => import('./components/member'));
const Dashboard = lazy(() => import('./components/dashboard'));
const ContactUs = lazy(() => import('./components/contactUs'));
const ReportSession = lazy(() => import('./components/Report'));
const MyFamily = lazy(() => import('./components/Nurturing/myFamily'));
const AllFamilies = lazy(() => import('./components/Nurturing/allFamilies'));
const Parents = lazy(() => import('./components/Nurturing/Admin/Parents'));
const Test = lazy(() => import('./components/test'));
const Role = lazy(() => import('./components/Admin/Roles'));
const BuyLesson = lazy(() => import('./components/SabbathScl/buyLesson'));
const Subscribers = lazy(() => import('./components/SabbathScl/Admin/subscribers'));
const Tickets = lazy(() => import('./components/Tickets/tickets'));
const Error = lazy(() => import('./components/Error404'));
const UploadResources = lazy(() => import('./components/Admin/uploadResources'));
const CreateTicket = lazy(() => import('./components/Tickets/Admin/createTicket'));
const Resources = lazy(() => import('./components/resources'));
const ManageTicket = lazy(() => import('./components/Tickets/Admin/manageTicket'));
const MemberContribution = lazy(() => import('./components/Treasury/MemberContribution'));
const Teachers = lazy(() => import('./components/SabbathScl/Admin/Teachers'));
const MakePayments = lazy(() => import('./components/Treasury/MakePayments'));
const MyAccount = lazy(() => import('./components/MyAccount'));
const ChangePassword = lazy(() => import('./components/ChangePassword'));
const GiveEmail = lazy(() => import('./components/reset_give_email'));
const GiveCode = lazy(() => import('./components/reset_code'));
const ResetPassword = lazy(() => import('./components/reset_password'));
const BooksManager = lazy(() => import('./components/publishing/Admin/booksManager'));
const BooksList = lazy(() => import('./components/publishing/booksList'));
const Gallery = lazy(() => import('./components/gallery'));
const Visitation = lazy(() => import('./components/Nurturing/Visitation'));
const GenerateFamilyV2 = lazy(() => import('./components/Nurturing/Admin/GenerateFamiliesV2'));
const LipaMdogoMdogo = lazy(() => import('./components/Tickets/lipamdogomdogo'));

// Loading Spinner Component
const PageLoader = () => (
  <div
    className="d-flex flex-column align-items-center justify-content-center"
    style={{ minHeight: '60vh', width: '100%' }}
  >
    <ReactLoading type="spin" color="#228bdf" height={50} width={50} />
    <p
      className="mt-3 font-weight-bold"
      style={{ color: '#228bdf', fontSize: '1.1rem' }}
    >
      Loading...
    </p>
  </div>
);

function App() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const token = useSelector((state) => state.auth.token);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const baseurl = mode
    ? 'https://backend.portal.jkusdachurch.org/general/'
    : 'http://127.0.0.1:8000/general/';

  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (token) {
    config.headers['Authorization'] = `Token ${token}`;
  }

  useEffect(() => {
    setIsLoaded(false);
    if (user && isAuthenticated) {
      axios
        .get(baseurl + 'check-authorization/', config)
        .then(() => {
          setIsLoaded(true);
        })
        .catch(() => {
          dispatch(logoutUser());
          setIsLoaded(true);
        });
    } else {
      setIsLoaded(true);
    }
  }, []);

  return (
    <Router>
      {isAuthenticated ? <AuthNav /> : <HomeNav />}
      <br />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            exact
            path="/"
            element={
              user == null ? (
                <Home />
              ) : isLoaded ? (
                isAuthenticated ? (
                  <Dashboard />
                ) : (
                  <Home />
                )
              ) : (
                <AuthLoading />
              )
            }
          />
          <Route exact path="/home" element={<Home />} />
          <Route exact path="/register" element={<Register />} />
          <Route exact path="/login" element={<Login />} />
          <Route exact path="/member-register" element={<MemberRegister />} />
          <Route exact path="/dashboard" element={<Dashboard />} />
          <Route exact path="/contact-us" element={<ContactUs />} />
          <Route exact path="/report-session" element={<ReportSession />} />
          <Route exact path="/my-family" element={<MyFamily />} />
          <Route exact path="/all-families" element={<AllFamilies />} />
          <Route exact path="/admin-generate-families" element={<GenerateFamilyV2 />} />
          <Route exact path="/admin-filter-parents" element={<Parents />} />
          <Route exact path="/admin-general-roless" element={<Role />} />
          <Route exact path="/admin-upload-resources" element={<UploadResources />} />
          <Route exact path="/resources" element={<Resources />} />
          <Route exact path="/buy-lesson" element={<BuyLesson />} />
          <Route exact path="/lesson-subscribers" element={<Subscribers />} />
          <Route exact path="/teachers" element={<Teachers />} />
          <Route exact path="/create-ticket" element={<CreateTicket />} />
          <Route exact path="/tickets" element={<Tickets />} />
          <Route exact path="/manage-ticket" element={<ManageTicket />} />
          <Route exact path="/my-contributions" element={<MemberContribution />} />
          <Route exact path="/make-payments" element={<MakePayments />} />
          <Route exact path="/my-account" element={<MyAccount />} />
          <Route exact path="/change-password" element={<ChangePassword />} />
          <Route exact path="/reset-give-email" element={<GiveEmail />} />
          <Route exact path="/reset-give-code" element={<GiveCode />} />
          <Route exact path="/reset-password" element={<ResetPassword />} />
          <Route exact path="/books-manager" element={<BooksManager />} />
          <Route exact path="/publishing" element={<BooksList />} />
          <Route exact path="/gallery" element={<Gallery />} />
          <Route exact path="/test" element={<Test />} />
          <Route exact path="/visitation" element={<Visitation />} />
          <Route exact path="/lipamdogomdogo" element={<LipaMdogoMdogo />} />
          <Route path="*" element={<Error />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;